import { useEffect, useState } from "react";
import type { AssistantProfile } from "@ai-sales/contracts";
import { SparklesIcon } from "./icons.js";

const maxAvatarBytes = 512 * 1024;
const supportedAvatarTypes = new Set(["image/png", "image/jpeg", "image/webp"]);

export function AssistantProfileSettings(props: {
  profile: AssistantProfile;
  busy: boolean;
  onSave: (
    input: Pick<AssistantProfile, "name" | "description" | "welcomeMessage"> & { avatarUrl?: string }
  ) => Promise<void>;
}) {
  const [draft, setDraft] = useState(() => ({
    name: props.profile.name,
    description: props.profile.description,
    welcomeMessage: props.profile.welcomeMessage,
    avatarUrl: props.profile.avatarUrl ?? ""
  }));
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    setDraft({
      name: props.profile.name,
      description: props.profile.description,
      welcomeMessage: props.profile.welcomeMessage,
      avatarUrl: props.profile.avatarUrl ?? ""
    });
  }, [props.profile]);

  const chooseAvatar = (file?: File) => {
    setLocalError("");
    if (!file) return;
    if (!supportedAvatarTypes.has(file.type)) {
      setLocalError("助手头像仅支持 PNG、JPEG 或 WebP");
      return;
    }
    if (file.size > maxAvatarBytes) {
      setLocalError("助手头像不能超过 512KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setDraft((current) => ({
      ...current,
      avatarUrl: typeof reader.result === "string" ? reader.result : current.avatarUrl
    }));
    reader.onerror = () => setLocalError("头像读取失败，请重新选择");
    reader.readAsDataURL(file);
  };

  return <section className="admin-card assistant-profile-settings" aria-labelledby="assistant-profile-title">
    <header className="assistant-profile-settings__header">
      <div className="assistant-profile-settings__avatar">
        {draft.avatarUrl
          ? <img src={draft.avatarUrl} alt="" />
          : <SparklesIcon size={24} />}
      </div>
      <div>
        <h2 id="assistant-profile-title">AI 助手品牌设置</h2>
        <p className="muted">名称、头像和欢迎语会统一应用到独立 Web 与 Salesforce 嵌入端。</p>
      </div>
    </header>
    <div className="model-form">
      <label>助手名称<input value={draft.name} maxLength={80} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} /></label>
      <label>简短描述<textarea rows={2} maxLength={240} value={draft.description} onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))} /></label>
      <label>欢迎语<textarea rows={4} maxLength={1000} value={draft.welcomeMessage} onChange={(event) => setDraft((current) => ({ ...current, welcomeMessage: event.target.value }))} /></label>
      <label className="profile-upload">
        助手头像
        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => chooseAvatar(event.target.files?.[0])} />
        <small>PNG、JPEG 或 WebP，不超过 512KB</small>
      </label>
      {draft.avatarUrl ? <button className="text-button" type="button" onClick={() => setDraft((current) => ({ ...current, avatarUrl: "" }))}>恢复默认助手头像</button> : null}
      {localError ? <div className="admin-error-banner" role="alert">{localError}</div> : null}
      <button
        className="button button--primary"
        type="button"
        disabled={props.busy || !draft.name.trim() || !draft.welcomeMessage.trim()}
        onClick={() => void props.onSave({
          name: draft.name.trim(),
          description: draft.description.trim(),
          welcomeMessage: draft.welcomeMessage.trim(),
          ...(draft.avatarUrl ? { avatarUrl: draft.avatarUrl } : {})
        })}
      >{props.busy ? "保存中…" : "保存助手设置"}</button>
    </div>
  </section>;
}
